import React, { useState } from "react";
import { supabase } from "../lib/supabaseClient";

interface ContactModalProps {
    isOpen: boolean;
    onClose: () => void;
}

function ContactModal({ isOpen, onClose }: ContactModalProps) {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus('idle');

        try {
            // Save contact message via API
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    subject: formData.subject,
                    message: formData.message
                })
            });

            if (!response.ok) {
                throw new Error('Failed to submit contact form');
            }

            // Email sending is now handled by the backend API
            setSubmitStatus('success');
            setFormData({ name: '', email: '', subject: '', message: '' });
            setTimeout(() => {
                onClose();
                setSubmitStatus('idle');
            }, 2000);

        } catch (error) {
            console.error('Error submitting contact form:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1050,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem'
                }}
            >
                {/* Modal Content */}
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        maxWidth: '600px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
                        position: 'relative'
                    }}
                >
                    {/* Header */}
                    <div style={{
                        backgroundColor: '#1A2940',
                        padding: '2rem',
                        borderTopLeftRadius: '12px',
                        borderTopRightRadius: '12px',
                        position: 'relative'
                    }}>
                        <button
                            onClick={onClose}
                            style={{
                                position: 'absolute',
                                top: '1rem',
                                right: '1rem',
                                background: 'transparent',
                                border: 'none',
                                color: '#ffffff',
                                fontSize: '1.5rem',
                                cursor: 'pointer',
                                padding: '0.5rem',
                                lineHeight: '1',
                                opacity: 0.7,
                                transition: 'opacity 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                        >
                            &times;
                        </button>
                        <h2 style={{
                            color: '#ffffff',
                            fontSize: '2rem',
                            fontWeight: 'bold',
                            marginBottom: '0.5rem'
                        }}>
                            Contact Us
                        </h2>
                        <p style={{
                            color: 'rgba(255, 255, 255, 0.8)',
                            fontSize: '1rem',
                            margin: 0
                        }}>
                            Have questions or feedback? We'd love to hear from you.
                        </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} style={{ padding: '2rem' }}>
                        {/* Name Field */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label
                                htmlFor="name"
                                style={{
                                    display: 'block',
                                    color: '#1A2940',
                                    fontWeight: '600',
                                    fontSize: '0.95rem',
                                    marginBottom: '0.5rem'
                                }}
                            >
                                Name *
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    fontSize: '1rem',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    outline: 'none',
                                    transition: 'border-color 0.2s'
                                }}
                                onFocus={(e) => e.currentTarget.style.borderColor = '#60a5fa'}
                                onBlur={(e) => e.currentTarget.style.borderColor = '#dee2e6'}
                            />
                        </div>

                        {/* Email Field */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label
                                htmlFor="email"
                                style={{
                                    display: 'block',
                                    color: '#1A2940',
                                    fontWeight: '600',
                                    fontSize: '0.95rem',
                                    marginBottom: '0.5rem'
                                }}
                            >
                                Email *
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    fontSize: '1rem',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    outline: 'none',
                                    transition: 'border-color 0.2s'
                                }}
                                onFocus={(e) => e.currentTarget.style.borderColor = '#60a5fa'}
                                onBlur={(e) => e.currentTarget.style.borderColor = '#dee2e6'}
                            />
                        </div>

                        {/* Subject Field */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label
                                htmlFor="subject"
                                style={{
                                    display: 'block',
                                    color: '#1A2940',
                                    fontWeight: '600',
                                    fontSize: '0.95rem',
                                    marginBottom: '0.5rem'
                                }}
                            >
                                Subject *
                            </label>
                            <input
                                type="text"
                                id="subject"
                                name="subject"
                                value={formData.subject}
                                onChange={handleChange}
                                required
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    fontSize: '1rem',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    outline: 'none',
                                    transition: 'border-color 0.2s'
                                }}
                                onFocus={(e) => e.currentTarget.style.borderColor = '#60a5fa'}
                                onBlur={(e) => e.currentTarget.style.borderColor = '#dee2e6'}
                            />
                        </div>

                        {/* Message Field */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label
                                htmlFor="message"
                                style={{
                                    display: 'block',
                                    color: '#1A2940',
                                    fontWeight: '600',
                                    fontSize: '0.95rem',
                                    marginBottom: '0.5rem'
                                }}
                            >
                                Message *
                            </label>
                            <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                required
                                rows={5}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    fontSize: '1rem',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    outline: 'none',
                                    transition: 'border-color 0.2s',
                                    resize: 'vertical',
                                    fontFamily: 'inherit'
                                }}
                                onFocus={(e) => e.currentTarget.style.borderColor = '#60a5fa'}
                                onBlur={(e) => e.currentTarget.style.borderColor = '#dee2e6'}
                            />
                        </div>

                        {/* Status Messages */}
                        {submitStatus === 'success' && (
                            <div style={{
                                padding: '1rem',
                                backgroundColor: '#d4edda',
                                color: '#155724',
                                borderRadius: '6px',
                                marginBottom: '1rem',
                                fontSize: '0.95rem'
                            }}>
                                Thank you for contacting us! We'll get back to you soon.
                            </div>
                        )}

                        {submitStatus === 'error' && (
                            <div style={{
                                padding: '1rem',
                                backgroundColor: '#f8d7da',
                                color: '#721c24',
                                borderRadius: '6px',
                                marginBottom: '1rem',
                                fontSize: '0.95rem'
                            }}>
                                Something went wrong. Please try again later.
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            style={{
                                width: '100%',
                                padding: '1rem',
                                backgroundColor: isSubmitting ? '#94a3b8' : '#1A2940',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '1rem',
                                fontWeight: '600',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                transition: 'background-color 0.2s',
                                opacity: isSubmitting ? 0.7 : 1
                            }}
                            onMouseEnter={(e) => {
                                if (!isSubmitting) {
                                    e.currentTarget.style.backgroundColor = '#2c3e5a';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isSubmitting) {
                                    e.currentTarget.style.backgroundColor = '#1A2940';
                                }
                            }}
                        >
                            {isSubmitting ? 'Sending...' : 'Send Message'}
                        </button>
                    </form>
                </div>
            </div>
        </>
    );
}

export default ContactModal;
